import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Calendar, MessageSquare, Package as PackageIcon, Mail, ArrowLeft, Check, X, Trash2, Plus, Loader2, Star, TrendingUp, Users, Coins, LogOut, Activity as ActivityIcon, KeyRound, FileSpreadsheet, FileText, ShoppingBag, Award, Truck, Ticket, Settings as SettingsIcon, Upload, CreditCard, Receipt, Phone, Map, IdCard } from 'lucide-react';
import { adminApi, getToken, clearToken } from '../lib/api';
import AdminLogin from './AdminLogin';
import ChangePasswordModal from '../components/ChangePasswordModal';
import UsersManagement from '../components/UsersManagement';
import ActivityFeed from '../components/ActivityFeed';
import NotificationsBell from '../components/NotificationsBell';
import DashboardCharts from '../components/DashboardCharts';
import ShopManagement from '../components/ShopManagement';
import LoyaltyPanel from '../components/LoyaltyPanel';
import OrdersManagement from '../components/OrdersManagement';
import CouponsManagement from '../components/CouponsManagement';
import SiteSettings from '../components/SiteSettings';
import PaymentSettings from '../components/PaymentSettings';
import Transactions from '../components/Transactions';
import ContactSettings from '../components/ContactSettings';
import LagoonCardsManagement from '../components/LagoonCardsManagement';
import MembersManagement from '../components/MembersManagement';

const ROLE_META = {
  super_admin: { label: 'Super Admin', color: 'bg-orange-100 text-[#E89534]' },
  comptable: { label: 'Comptable', color: 'bg-blue-100 text-blue-700' },
  guide: { label: 'Guide', color: 'bg-emerald-100 text-emerald-700' }
};

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const StatusBadge = ({ status }) => {
  const map = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'En attente' },
    confirmed: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Confirmée' },
    completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Terminée' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Annulée' },
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Approuvé' },
    rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' }
  };
  const s = map[status] || { bg: 'bg-slate-100', text: 'text-slate-700', label: status };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
};

const Overview = () => {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.getDashboard().then(setData); }, []);
  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;

  const cards = [
    { label: 'Réservations', value: data.reservations.total, icon: Calendar, color: 'bg-blue-500' },
    { label: 'Revenus totaux', value: `${formatFCFA(data.revenue)} F`, icon: Coins, color: 'bg-emerald-500' },
    { label: 'Confirmées', value: data.reservations.confirmed, icon: Check, color: 'bg-[#F4A340]' },
    { label: 'Avis en attente', value: data.reviews.pending, icon: MessageSquare, color: 'bg-purple-500' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 border border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center text-white`}>
                <c.icon className="w-5 h-5" />
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900">{c.value}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Réservations récentes</h3>
        <div className="space-y-3">
          {data.recent_reservations.length === 0 && <p className="text-slate-500 text-sm">Aucune réservation.</p>}
          {data.recent_reservations.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors">
              <div>
                <div className="font-semibold text-slate-900">{r.customer.fullName} · {r.reference}</div>
                <div className="text-xs text-slate-500">{r.package_name} · {r.date} {r.time_slot} · {r.passengers} pers.</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm font-bold text-slate-900">{formatFCFA(r.total_amount)} F</div>
                <StatusBadge status={r.status} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <DashboardCharts />
    </div>
  );
};

const Reservations = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  const load = async () => {
    setLoading(true);
    const data = await adminApi.getReservations(filter);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    await adminApi.updateReservation(id, { status });
    load();
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  };

  const exportXlsx = async () => {
    setExporting('xlsx');
    try {
      const blob = await adminApi.exportReservationsXlsx();
      downloadBlob(blob, `reservations-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally { setExporting(''); }
  };

  const exportPdf = async () => {
    setExporting('pdf');
    try {
      const blob = await adminApi.exportReportPdf();
      downloadBlob(blob, `rapport-pierreedois-${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally { setExporting(''); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {['', 'pending', 'confirmed', 'completed', 'cancelled'].map((f) => (
            <button
              key={f || 'all'}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'
              }`}
            >{f ? { pending: 'En attente', confirmed: 'Confirmées', completed: 'Terminées', cancelled: 'Annulées' }[f] : 'Toutes'}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportXlsx}
            disabled={exporting !== ''}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-full transition-colors shadow-sm"
          >
            {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Excel
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting !== ''}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-full transition-colors shadow-sm"
          >
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left">
                {['Référence', 'Client', 'Formule', 'Date', 'Total', 'Paiement', 'Statut', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-[#F4A340]" /></td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Aucune réservation</td></tr>}
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.reference}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.customer.fullName}</div>
                    <div className="text-xs text-slate-500">{r.customer.email}</div>
                  </td>
                  <td className="px-4 py-3">{r.package_name}</td>
                  <td className="px-4 py-3">{r.date}<div className="text-xs text-slate-500">{r.time_slot} · {r.passengers} pers.</div></td>
                  <td className="px-4 py-3 font-bold">{formatFCFA(r.total_amount)} F</td>
                  <td className="px-4 py-3 text-xs">
                    {r.payment?.status === 'paid' ? (
                      <span className="text-emerald-600 font-semibold">✓ {r.payment.provider?.toUpperCase()}</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3">
                    <select
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none focus:border-[#F4A340]"
                    >
                      <option value="pending">En attente</option>
                      <option value="confirmed">Confirmée</option>
                      <option value="completed">Terminée</option>
                      <option value="cancelled">Annulée</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Reviews = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', location: '', rating: 5, comment: '', status: 'approved' });
  const [showForm, setShowForm] = useState(false);

  const load = async () => setItems(await adminApi.getReviews());
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.comment) return;
    await adminApi.createReview(form);
    setForm({ name: '', location: '', rating: 5, comment: '', status: 'approved' });
    setShowForm(false);
    load();
  };

  const approve = async (id) => { await adminApi.updateReview(id, { status: 'approved' }); load(); };
  const reject = async (id) => { await adminApi.updateReview(id, { status: 'rejected' }); load(); };
  const remove = async (id) => { if (window.confirm('Supprimer cet avis ?')) { await adminApi.deleteReview(id); load(); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold">Avis clients ({items.length})</h3>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full transition-colors">
          <Plus className="w-4 h-4" /> Ajouter un avis
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            <input placeholder="Localisation" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((r) => (
              <button key={r} onClick={() => setForm({ ...form, rating: r })}>
                <Star className={`w-6 h-6 ${r <= form.rating ? 'fill-[#F4A340] text-[#F4A340]' : 'text-slate-300'}`} />
              </button>
            ))}
          </div>
          <textarea placeholder="Commentaire" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={3} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none resize-none" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={submit} className="px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">Publier</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F4A340] to-[#E89534] flex items-center justify-center text-white font-bold text-sm">{r.avatar}</div>
                <div>
                  <div className="font-bold text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.location}</div>
                </div>
              </div>
              <StatusBadge status={r.status} />
            </div>
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#F4A340] text-[#F4A340]" />)}
            </div>
            <p className="text-sm text-slate-700 mb-4">{r.comment}</p>
            <div className="flex gap-2">
              {r.status !== 'approved' && <button onClick={() => approve(r.id)} className="px-3 py-1.5 rounded-full text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Approuver</button>}
              {r.status !== 'rejected' && <button onClick={() => reject(r.id)} className="px-3 py-1.5 rounded-full text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold flex items-center gap-1"><X className="w-3 h-3" /> Rejeter</button>}
              <button onClick={() => remove(r.id)} className="ml-auto px-3 py-1.5 rounded-full text-xs bg-red-50 text-red-600 hover:bg-red-100 font-semibold flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Packages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const emptyPkg = {
    name: '', subtitle: '', duration: '', price: 0,
    priceNote: 'F CFA / personne', priceExtra: '',
    stars: 1, popular: false, image: '', features: [''],
    cta: 'Réserver'
  };
  const [form, setForm] = useState(emptyPkg);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.getPackages()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ ...emptyPkg, features: [''] });
    setEditing(null);
    setShowNew(true);
    setError('');
  };

  const startEdit = (p) => {
    // Use price_ht for admin edition (DB stores HT, public shows TTC in `price`)
    setForm({
      ...p,
      price: p.price_ht ?? p.price,
      features: p.features && p.features.length ? p.features : ['']
    });
    setEditing(p);
    setShowNew(false);
    setError('');
  };

  const cancel = () => { setEditing(null); setShowNew(false); setForm(emptyPkg); setError(''); };

  const save = async () => {
    setError('');
    if (!form.name || !form.duration || !form.price) {
      return setError('Nom, durée et prix sont obligatoires');
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseInt(form.price) || 0,
        stars: parseInt(form.stars) || 1,
        features: form.features.filter(f => f && f.trim()),
        cta: form.cta || `Réserver ${form.name}`,
        priceExtra: form.priceExtra || null
      };
      if (editing) {
        await adminApi.updatePackage(editing.id, payload);
      } else {
        await adminApi.createPackage(payload);
      }
      cancel();
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (window.confirm(`Supprimer la formule "${p.name}" ?`)) {
      await adminApi.deletePackage(p.id);
      load();
    }
  };

  const togglePopular = async (p) => { await adminApi.updatePackage(p.id, { popular: !p.popular }); load(); };
  const toggleActive = async (p) => { await adminApi.updatePackage(p.id, { active: !p.active }); load(); };

  const updateFeature = (idx, val) => {
    const arr = [...form.features]; arr[idx] = val;
    setForm({ ...form, features: arr });
  };
  const addFeature = () => setForm({ ...form, features: [...form.features, ''] });
  const removeFeature = (idx) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });
  };

  const showForm = showNew || editing;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Formules de balade</h3>
          <p className="text-sm text-slate-500">{items.length} formule{items.length > 1 ? 's' : ''} · gérez le catalogue affiché sur le site</p>
        </div>
        <button onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">
          <Plus className="w-4 h-4" /> Nouvelle formule
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border-2 border-[#F4A340]/30 space-y-3">
          <h4 className="font-bold text-slate-900">{editing ? `Modifier "${editing.name}"` : 'Nouvelle formule'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nom *</label>
              <input placeholder="Excellence" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Sous-titre</label>
              <input placeholder="Une évasion romantique" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Durée *</label>
              <input placeholder="2 H" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Étoiles (1-5)</label>
              <input type="number" min="1" max="5" value={form.stars} onChange={(e) => setForm({ ...form, stars: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Prix HT (F CFA) *</label>
              <input type="number" placeholder="8475" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
              {form.price > 0 && (
                <p className="text-[10px] text-slate-500 mt-1">
                  = <span className="font-bold text-[#E89534]">{new Intl.NumberFormat('fr-FR').format(Math.round((parseInt(form.price) || 0) * 1.18))} F TTC</span> affichés au client (TVA 18% incluse)
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Unité de prix</label>
              <input placeholder="F CFA / couple" value={form.priceNote} onChange={(e) => setForm({ ...form, priceNote: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Note de prix (optionnel)</label>
              <input placeholder="Ex: 25 000 F à deux · 15 000 F à partir de 5 passagers" value={form.priceExtra || ''} onChange={(e) => setForm({ ...form, priceExtra: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Photo de la formule</label>
              <div className="flex gap-3 items-start">
                <div className="w-32 h-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {form.image ? (
                    <img src={form.image} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <PackageIcon className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file" accept="image/*" id="pkg-file-input" className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      if (file.size > 3 * 1024 * 1024) { alert('Image trop lourde (max 3 Mo)'); return; }
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement('canvas');
                          const scale = Math.min(1, 1200 / img.width);
                          canvas.width = img.width * scale; canvas.height = img.height * scale;
                          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                          setForm(f => ({ ...f, image: canvas.toDataURL('image/jpeg', 0.85) }));
                        };
                        img.src = ev.target.result;
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <button type="button" onClick={() => document.getElementById('pkg-file-input').click()} className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-semibold w-full justify-center">
                    <Upload className="w-4 h-4" /> Téléverser une photo
                  </button>
                  <input placeholder="Ou coller une URL d'image" value={form.image?.startsWith('data:') ? '' : (form.image || '')} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full px-4 py-2 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm" />
                  {form.image && (
                    <button type="button" onClick={() => setForm({ ...form, image: '' })} className="text-xs text-red-600 hover:underline">Retirer l'image</button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1">Max 3 Mo · redimensionnée à 1200 px</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Texte du bouton</label>
              <input placeholder="Réserver Excellence" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Caractéristiques incluses</label>
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input value={f} onChange={(e) => updateFeature(i, e.target.value)} placeholder={`Point ${i + 1}`} className="flex-1 px-4 py-2 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm" />
                  <button type="button" onClick={() => removeFeature(i)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" disabled={form.features.length <= 1}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addFeature} className="text-xs text-[#E89534] hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Ajouter une caractéristique
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} className="accent-[#F4A340]" />
              Marquer "LE PLUS DEMANDÉ"
            </label>
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={cancel} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {editing ? 'Enregistrer' : 'Créer la formule'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {p.image ? (
                <img src={p.image} alt={p.name} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-slate-100 flex items-center justify-center text-slate-400">
                  <PackageIcon className="w-10 h-10" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-slate-900">{p.name}</h3>
                  {p.popular && <span className="text-xs px-2 py-0.5 rounded-full bg-[#F4A340] text-white font-semibold">Populaire</span>}
                  {!p.active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">Inactive</span>}
                </div>
                <p className="text-xs text-slate-500 mb-2">{p.duration} · {p.subtitle}</p>
                <div className="text-2xl font-bold text-[#E89534] mb-2">{formatFCFA(p.price)} <span className="text-xs font-normal text-slate-500">{p.priceNote}</span></div>
                {p.features && p.features.length > 0 && (
                  <ul className="text-xs text-slate-600 space-y-1 mb-3 max-h-20 overflow-y-auto">
                    {p.features.slice(0, 3).map((f, i) => <li key={i} className="truncate">• {f}</li>)}
                    {p.features.length > 3 && <li className="text-slate-400">+ {p.features.length - 3} autres</li>}
                  </ul>
                )}
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => startEdit(p)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-semibold">
                    Éditer
                  </button>
                  <button onClick={() => togglePopular(p)} title={p.popular ? 'Retirer populaire' : 'Marquer populaire'} className="px-3 py-2 rounded-full bg-orange-50 hover:bg-orange-100 text-[#E89534]">
                    <Star className={`w-4 h-4 ${p.popular ? 'fill-[#F4A340]' : ''}`} />
                  </button>
                  <button onClick={() => remove(p)} className="px-3 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button onClick={() => toggleActive(p)} className="mt-2 w-full text-xs text-slate-500 hover:text-[#E89534]">
                  {p.active ? 'Désactiver (masquer du site)' : 'Réactiver'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Emails = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { adminApi.getEmails().then(setItems); }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 overflow-hidden max-h-[70vh] overflow-y-auto">
        {items.length === 0 && <p className="p-6 text-slate-500 text-sm text-center">Aucun email envoyé (mock)</p>}
        {items.map((e) => (
          <button
            key={e.id}
            onClick={() => setSelected(e)}
            className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected?.id === e.id ? 'bg-orange-50' : ''}`}
          >
            <div className="font-semibold text-sm text-slate-900 truncate">{e.subject}</div>
            <div className="text-xs text-slate-500 mt-1 truncate">À: {e.to}</div>
            <div className="text-xs text-slate-400 mt-1">{new Date(e.sent_at).toLocaleString('fr-FR')}</div>
          </button>
        ))}
      </div>
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 min-h-[300px]">
        {!selected && <p className="text-slate-500 text-center py-12">Sélectionnez un email pour le lire</p>}
        {selected && (
          <div>
            <div className="border-b pb-3 mb-4">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Sujet</div>
              <div className="font-bold text-slate-900">{selected.subject}</div>
              <div className="text-sm text-slate-600 mt-2">À: {selected.to}</div>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{selected.body}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [tab, setTab] = useState('overview');
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());
  const [user, setUser] = useState(null);
  const [showChangePwd, setShowChangePwd] = useState(false);

  useEffect(() => {
    if (getToken()) {
      adminApi.me()
        .then((u) => { setUser(u); setAuthed(true); })
        .catch(() => { clearToken(); setAuthed(false); })
        .finally(() => setChecking(false));
    }
  }, []);

  const refreshMe = async () => {
    try { setUser(await adminApi.me()); } catch (e) { /* ignore */ }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F4A340]" />
      </div>
    );
  }

  if (!authed || !user) {
    return <AdminLogin onSuccess={async () => { await refreshMe(); setAuthed(true); }} />;
  }

  const role = user.role;
  const isSuper = role === 'super_admin';
  const canReviews = isSuper || role === 'comptable';

  const allTabs = [
    { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard, cmp: Overview, roles: ['super_admin', 'comptable', 'guide'] },
    { id: 'reservations', label: 'Réservations', icon: Calendar, cmp: Reservations, roles: ['super_admin', 'comptable', 'guide'] },
    { id: 'reviews', label: 'Avis', icon: MessageSquare, cmp: Reviews, roles: ['super_admin', 'comptable'] },
    { id: 'packages', label: 'Formules', icon: PackageIcon, cmp: Packages, roles: ['super_admin'] },
    { id: 'shop', label: 'Boutique', icon: ShoppingBag, cmp: ShopManagement, roles: ['super_admin'] },
    { id: 'orders', label: 'Commandes', icon: Truck, cmp: OrdersManagement, roles: ['super_admin', 'comptable'] },
    { id: 'loyalty', label: 'Fidélité', icon: Award, cmp: LoyaltyPanel, roles: ['super_admin', 'comptable'] },
    { id: 'members', label: 'Membres', icon: IdCard, cmp: MembersManagement, roles: ['super_admin', 'comptable'] },
    { id: 'coupons', label: 'Coupons', icon: Ticket, cmp: CouponsManagement, roles: ['super_admin', 'comptable'] },
    { id: 'settings', label: 'Paramètres', icon: SettingsIcon, cmp: SiteSettings, roles: ['super_admin'] },
    { id: 'lagoon', label: 'Carnet lagune', icon: Map, cmp: LagoonCardsManagement, roles: ['super_admin'] },
    { id: 'contact', label: 'Contact', icon: Phone, cmp: ContactSettings, roles: ['super_admin'] },
    { id: 'payment', label: 'Paiement', icon: CreditCard, cmp: PaymentSettings, roles: ['super_admin'] },
    { id: 'transactions', label: 'Transactions', icon: Receipt, cmp: Transactions, roles: ['super_admin', 'comptable'] },
    { id: 'emails', label: 'Emails', icon: Mail, cmp: Emails, roles: ['super_admin', 'comptable'] },
    { id: 'users', label: 'Utilisateurs', icon: Users, cmp: () => <UsersManagement currentUsername={user.username} />, roles: ['super_admin'] },
    { id: 'activity', label: 'Activité', icon: ActivityIcon, cmp: ActivityFeed, roles: ['super_admin'] }
  ];
  const tabs = allTabs.filter((t) => t.roles.includes(role));
  const currentTab = tabs.find((t) => t.id === tab) || tabs[0];
  const Current = currentTab.cmp;

  const roleMeta = ROLE_META[role] || { label: role, color: 'bg-slate-100 text-slate-700' };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#E89534] transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> Retour au site
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rotate-45 bg-[#F4A340] rounded-md flex items-center justify-center flex-shrink-0">
                <span className="-rotate-45 text-white font-bold text-sm italic">p</span>
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">Admin Dashboard</div>
                <div className="text-[10px] tracking-widest text-slate-500">PIERRE & EDOIS</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationsBell />
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs font-medium text-slate-700">{user.username}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${roleMeta.color}`}>{roleMeta.label}</span>
            </div>
            <button
              onClick={() => setShowChangePwd(true)}
              title="Changer mot de passe"
              className="p-2 rounded-full bg-white border border-slate-200 hover:border-[#F4A340] hover:text-[#E89534] transition-colors"
            >
              <KeyRound className="w-4 h-4" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-sm font-medium text-slate-700 transition-colors"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
                tab === t.id ? 'bg-slate-900 text-white shadow-md' : 'bg-white border border-slate-200 text-slate-700 hover:border-[#F4A340]'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <Current />
      </div>
      <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
    </div>
  );
};

export default AdminDashboard;
